// @flow
import server from 'server2';
import _ from 'lodash';
import { JSableOmitPrivate } from 'jsable';
import invariant from 'invariant';
import type { Observable, ObservableArray, PureComputed } from 'rgs-knockout';

export type AdaptiveTableDataResponse<T> = {
	Data: {
		Items: Array<T>,
		Total: number
	}
}

export type DataProviderParams = {
	defaultUrlParams?: Array<string>,
	defaultQueryParams?: Object,
	dynamicQueryParams?: Object,
	getData?: (params: Object) => Promise<AdaptiveTableDataResponse>,
	mockData?: Object,
	localData?: PureComputed<Array<Object>> | Observable<Array<Object>> | ObservableArray<Object>
		| Array<Object>
}

export class DataProvider
	extends JSableOmitPrivate {
	_localData: PureComputed<Array<Object>> | Observable<Array<Object>>
		| ObservableArray<Object>
		| Array<Object>;
	_defaultUrlParams: ?Array<string>;
	_defaultQueryParams: ?Object;
	_getData: ?(params: Object) => Promise<AdaptiveTableDataResponse>;
	_mockData: ?AdaptiveTableDataResponse;

	constructor (
		options: DataProviderParams,
	) {
		super();

		const {
			getData,
			defaultQueryParams = {},
			defaultUrlParams,
			mockData,
			localData = [],
		} = options;

		this._getData = getData;
		this._defaultUrlParams = defaultUrlParams;
		this._defaultQueryParams = defaultQueryParams;

		this._mockData = mockData;
		this._localData = localData;

		this._client = server.any.api;
	}

	async _fetchDataByDefaultParams (
		defaultUrlParams: Array<string>,
		queryParams: Object,
		defaultQueryParams: ?Object = {}
	): Promise<AdaptiveTableDataResponse> {
		const response: { responseJSON: AdaptiveTableDataResponse } = await this._client.get(
			defaultUrlParams,
			{
				query: {
					...queryParams,
					..._.mapValues(defaultQueryParams,
						param => _.isFunction(param) ? param() : param),
				},
			},
		);

		return response.responseJSON;
	}

	async getData (
		queryParams: {
			from?: number,
			take?: number
		} = {}
	): Promise<AdaptiveTableDataResponse> {
		let responseJSON: AdaptiveTableDataResponse = {
			Data: {
				Items: [],
				Total: 0,
			},
		};
		const localData: any = _.isFunction(this._localData) ? this._localData() : this._localData;
		
		invariant(
			this._defaultUrlParams || this._getData || this._localData || this._mockData,
			`ko-component-adaptive-table: DataProvider: getData
			You should specify at least one way to get data.
			It can be "defaultUrlParams" or "getData" method or "localData" array
			or "mockData" if you want to mock whole response`
		);
		
		if (this._mockData){
			return this._mockData;
		}

		if (localData.length) {
			const unwrapedLocalData = _.map(localData, item => (item.toJS && item.toJS()) || item );

			responseJSON = {
				Data: {
					Items: _.slice(
						unwrapedLocalData,
						queryParams.from,
						queryParams.from + queryParams.take
					),
					Total: unwrapedLocalData.length,
				},
			};
		} else if (this._getData) {
			responseJSON = await this._getData(queryParams);
		} else if (this._defaultUrlParams) {
			responseJSON = await this._fetchDataByDefaultParams(
				this._defaultUrlParams,
				queryParams,
				this._defaultQueryParams
			);
		}

		return responseJSON;
	}
}
